<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <v-col v-if="mapPanelDataFetched">
      <card-title-row class="pl-0 pt-5" :translation-path="titleTranslationPath" :has-buttons="false" />
      <profiles-widget
        v-bind="profilesData"
        :is-add-profile-disabled="
          $refs[`steps${toolName}Wizard`] ? $refs[`steps${toolName}Wizard`].allStepsValid : true
        "
        :current-data="getProfilePayload()"
        use-user-permissions
        @changeCurrentProfile="changeCurrentProfile"
      ></profiles-widget>
      <steps-wizard
        :ref="`steps${toolName}Wizard`"
        result-below
        :steps="steps"
        :inputs="inputs"
        :params-proxy.sync="params"
        :is-analysis-done-proxy.sync="isAnalysisDone"
        :is-analysis-loading="isAnalysisLoading"
        :wizard-action-handler="turnOnAnalysis"
        :additional-is-data-valid="isObjectSelected"
        :custom-reset-handler="clearLastStep"
      >
        <template #analysisDoneCallback>
          <template v-if="isAnalysisDone">
            <v-row v-if="isToolOutpouring && responseData.time && responseData.volume" class="text-left mt-2">
              <v-col>
                <span>{{ $i18n.t('sidebar.upstreamSewage.sixHoursSewerValue', { time: responseData.time }) }}: </span>
                <span class="font-weight-bold">{{ responseData.volume }}m³</span>
              </v-col>
            </v-row>
            <v-row v-if="responseData.demand_day" class="text-left mt-2 mb-5">
              <v-col>
                <span>{{ $i18n.t('sidebar.upstreamSewage.dayAverageSum') }}: </span>
                <span class="font-weight-bold">{{ responseData.demand_day }}m³</span>
              </v-col>
            </v-row>
          </template>
        </template>
        <template #stepPrepend-last>
          <v-row class="ma-0">
            <v-col class="text-left px-0 pb-0">
              <v-tooltip :disabled="!isObjectSelected" bottom max-width="300">
                <template #activator="{ on }">
                  <span v-on="on">
                    <data-input
                      :dots-items="dotsItems"
                      :dots-items-custom-listeners="dotsItemsListeners"
                      ref="pipeIdButton"
                      :featuresSettings="featuresSettings"
                      @pushFeatures="selectPipe"
                      translation-path="sidebar.upstreamSewage.selectObject"
                      :data-type="{ name: 'features' }"
                      :disabled="
                        ($refs[`steps${toolName}Wizard`] ? !$refs[`steps${toolName}Wizard`].allStepsValid : true) ||
                          isObjectSelected
                      "
                    />
                  </span>
                </template>
                {{ $i18n.t('sidebar.upstreamSewage.onlyOneObject') }}
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row no-gutters class="mb-2">
            <v-col cols="auto">
              <italic-subheader
                class="text-left"
                translation-path="sidebar.upstreamSewage.selectStartingPoint"
              ></italic-subheader>
            </v-col>
          </v-row>
          <template v-if="isObjectSelected">
            <v-row class="mb-2">
              <v-col>
                <v-list class="py-0">
                  <v-list-item :key="params['analysis_object']" class="hover-list-element">
                    <v-list-item-content class="pa-0">
                      <v-list-item-title>
                        <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                          ID: {{ params['analysis_object'] }}
                        </v-subheader>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action class="flex-row">
                      <v-icon @click="zoomToFeature(params['analysis_object'])" color="rgba(0,0,0,.7)" size="19px">
                        mdi-magnify
                      </v-icon>
                      <v-icon @click="deleteFeature(params['analysis_object'])" color="rgba(0,0,0,.7)" size="19px">
                        mdi-close
                      </v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </template>
          <v-row class="align-baseline pt-5">
            <v-col cols="auto" class="d-flex py-0">
              <data-input v-bind="ignoreInvertedGradientsProps" v-model="params[ignoreInvertedGradientsProps.name]" />
            </v-col>
          </v-row>
          <v-row class="align-baseline">
            <v-col cols="auto" class="d-flex py-0">
              <data-input v-bind="stopAnalysisCheckboxProps" v-model="params[stopAnalysisCheckboxProps.name]" />
            </v-col>
          </v-row>
        </template>
      </steps-wizard>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import StepsWizard from '@/components/StepsWizard';
import MainLoader from '@/components/MainLoader';
import ItalicSubheader from '@/components/ItalicSubheader';
import DataInput from '@/components/DataInput';
import ProfilesWidget from '@/components/ProfilesWidget';
import { get, call } from 'vuex-pathify';

export default {
  name: 'UpstreamSewageWizard',
  components: {
    CardTitleRow,
    StepsWizard,
    MainLoader,
    ItalicSubheader,
    DataInput,
    ProfilesWidget,
  },
  props: {
    toolName: {
      type: String,
      required: true,
    },
    titleTranslationPath: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isAnalysisLoading: false,
    isAnalysisDone: false,
    responseData: {
      pipes: [],
    },
    params: {
      analysis_layer: undefined,
      analysis_object: undefined,
      stop_at_pumping_station: false,
      ignore_inverted_gradients: false,
    },
  }),

  methods: {
    ...call('modeling/upstreamSewage*Analysis'),
    ...call('modeling/*Sewage*Profil*'),

    async changeCurrentProfile(newProfile) {
      if (newProfile) {
        let currentProfileCopy = JSON.parse(JSON.stringify(newProfile));
        ['id', 'create_datetime', 'name', 'owner'].forEach(key => delete currentProfileCopy[key]);
        await this.$nextTick();
        this.params = { ...this.params, ...currentProfileCopy };
      }
    },
    getProfilePayload() {
      let paramsCopy = {
        ...this.params,
      };
      ['analysis_layer', 'analysis_object'].forEach(key => delete paramsCopy[key]);
      return paramsCopy;
    },
    selectFeatures(layerId, results) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: results,
        },
      });
    },
    deselectFeatures(layerId, featuresId) {
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: featuresId || [],
        },
      });
    },
    selectPipe(features) {
      if (!features || features.length === 0) {
        return;
      }
      this.params['analysis_object'] = features[0].id;
      this.params['analysis_layer'] = features[0].layerId;
      this.$refs[`pipeIdButton`]?.$refs['data-input']?.toggleOff();
      this.selectFeatures(this.params['analysis_layer'], [this.params['analysis_object']]);
      this.$root.$emit('setProjectLayerStyle', this.params['analysis_layer'], this.highlightedObjectStyle);
    },
    zoomToFeature(id, layerId = this.params['analysis_layer']) {
      this.$root.$emit('zoomToFeature', layerId, 'layers', id);
    },
    deleteFeature(id, layerId = this.params['analysis_layer']) {
      this.$root.$emit('setProjectLayerStyle', this.params['analysis_layer']);
      this.deselectFeatures(layerId, id ? [id] : []);
      this.params['analysis_layer'] = undefined;
      this.params['analysis_object'] = undefined;
      this.responseData = {
        pipes: [],
      };
      if (this.$route.name === `upstreamSewage${this.toolName}Layer`) {
        this.deselectFeatures(this.params['pipe_layer']);
        this.$router.push({ name: `upstreamSewage${this.toolName}` });
      }
    },
    clearLastStep() {
      this.params['stop_at_pumping_station'] = false;
      this.deleteFeature(this.params['analysis_object']);
    },
    async turnOnAnalysis() {
      this.isAnalysisLoading = true;
      try {
        let r = await this[`upstreamSewage${this.toolName}Analysis`](this.params);
        r.demand_day = parseFloat(r.demand_day)
          .toFixed(2)
          .toString();
        if (this.isToolOutpouring) {
          r.time = Math.floor(r.time).toString();
          r.volume = parseFloat(r.volume)
            .toFixed(2)
            .toString();
        }
        this.responseData = r;
        if (this.$route.params.lid) {
          this.deselectFeatures(this.params['pipe_layer']);
          this.selectFeatures(this.params['pipe_layer'], this.responseData.pipes);
        } else {
          await this.$router.push({
            name: `upstreamSewage${this.toolName}Layer`,
            params: { lid: this.params['pipe_layer'] },
          });
        }
        this.$root.$emit('setProjectLayerStyle', this.params['analysis_layer'], this.highlightedObjectStyle);
        this.isAnalysisDone = true;
      } finally {
        this.isAnalysisLoading = false;
      }
    },
  },
  mounted() {
    if (this.$route.name === `upstreamSewage${this.toolName}Layer`) {
      this.$router.push({ name: `upstreamSewage${this.toolName}` });
    }
    this.$root.$on('tableDataRowsFetched', () => {
      this.selectFeatures(this.params['pipe_layer'], this.responseData.pipes);
    });
  },
  beforeDestroy() {
    if (this.isAnalysisDone) this.deselectFeatures(this.params['pipe_layer']);
    if (this.isObjectSelected) this.deleteFeature(this.params['analysis_object']);
    this.$root.$off('tableDataRowsFetched');
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),

    sewageVolumeProfiles: get('modeling/sewageVolumeProfiles'),
    sewageOutpouringProfiles: get('modeling/sewageOutpouringProfiles'),
    isObjectSelected() {
      return !!this.params['analysis_object'];
    },
    isToolOutpouring() {
      return this.toolName === 'Outpouring';
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: `dialog.title.profiles.upstreamSewageAdd`,
          editProfileDialogTitle: `dialog.title.profiles.upstreamSewageEdit`,
          deleteProfileDialogTitle: `dialog.title.profiles.upstreamSewageDelete`,
        },
        profiles: this[`sewage${this.toolName}Profiles`],
        getProfiles: this[`getSewage${this.toolName}Profiles`],
        saveProfile: this[`saveSewage${this.toolName}Profile`],
        editProfile: this[`editSewage${this.toolName}Profile`],
        deleteProfile: this[`deleteSewage${this.toolName}Profile`],
      };
    },
    selectedObjectLabel() {
      if (this.isToolOutpouring) {
        return [this.responseData.time, this.responseData.volume].every(x => !!x)
          ? `${this.responseData.time}h ${this.responseData.volume}m³`
          : null;
      } else {
        return this.responseData.demand_day ? `${this.responseData.demand_day}m³` : null;
      }
    },
    highlightedObjectStyle() {
      return {
        selectedColors: {
          selectedColor: '#F00',
          haloColorSelected: '#F00',
          ...(this.selectedObjectLabel && {
            labelCustomText: this.selectedObjectLabel,
            labelsVisible: true,
          }),
        },
      };
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    sewerPipesLayers() {
      return this.projectLayers.filter(layer => layer.role === 'sewer_pipes_sanitary_sewerage');
    },
    sewerManholesLayers() {
      return this.projectLayers.filter(layer => layer.role === 'sewer_manholes_sanitary_sewerage');
    },
    sewerPumpsLayers() {
      return this.projectLayers.filter(layer => layer.role === 'sewer_pumps_sanitary_sewerage');
    },
    waterPartitionsLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_partitions');
    },
    watermetersLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_watermeters');
    },
    ignoreInvertedGradientsProps() {
      return {
        name: 'ignore_inverted_gradients',
        label: this.$i18n.t('sidebar.upstreamSewage.ignoreInvertedGradients'),
        dataType: { name: 'checkbox' },
        hasNameColumn: false,
        cols: '12',
      };
    },
    stopAnalysisCheckboxProps() {
      return {
        name: 'stop_at_pumping_station',
        label: this.$i18n.t('sidebar.upstreamSewage.stopAnalysisOnFirstPump'),
        dataType: { name: 'checkbox' },
        hasNameColumn: false,
        cols: '12',
      };
    },
    dotsItemsListeners() {
      return {
        selectSewerManhole: {
          handler: () => (this.params['analysis_layer'] = this.params.manhole_layer),
        },
        selectSewerPump: {
          handler: () => (this.params['analysis_layer'] = this.params.pump_layer),
        },
      };
    },
    dotsItems() {
      return [
        {
          name: 'selectSewerManhole',
        },
        {
          name: 'selectSewerPump',
        },
      ];
    },
    featuresSettings() {
      return {
        layers: {
          included: [this.params['analysis_layer']],
        },
      };
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.upstreamSewage.pipes',
        },
        {
          step: 2,
          translationPath: 'sidebar.upstreamSewage.manholes',
        },
        {
          step: 3,
          translationPath: 'sidebar.upstreamSewage.pumps',
        },
        {
          step: 4,
          translationPath: 'sidebar.upstreamSewage.partitions',
        },
        {
          step: 5,
          translationPath: 'sidebar.upstreamSewage.watermeters',
        },
        {
          step: 6,
          translationPath: 'sidebar.upstreamSewage.analysis',
        },
      ];
    },
    defaultInputProps() {
      return {
        dataType: { name: 'select' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
    inputs() {
      return [
        {
          name: 'pipe_layer',
          step: 1,
          ...(this.isToolOutpouring && { isLayer: true }),
          translationPath: 'sidebar.upstreamSewage.pipesLayer',
          items: this.sewerPipesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.sewerPipesLayers?.length === 0,
          tooltip: this.sewerPipesLayers?.length === 0 ? this.$i18n.t('sidebar.upstreamSewage.addPipesToRun') : null,
          ...this.defaultInputProps,
        },
        ...(this.isToolOutpouring
          ? [
              {
                name: 'pipe_length_attribute',
                step: 1,
                connectedLayer: 'pipe_layer',
                translationPath: 'sidebar.upstreamSewage.pipeLengthAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['pipe_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'pipe_diameter_attribute',
                step: 1,
                connectedLayer: 'pipe_layer',
                translationPath: 'sidebar.upstreamSewage.pipeDiameterAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['pipe_layer'],
                ...this.defaultInputProps,
              },
            ]
          : []),
        {
          name: 'manhole_layer',
          ...(this.isToolOutpouring && { isLayer: true }),
          step: 2,
          translationPath: 'sidebar.upstreamSewage.manholesLayer',
          items: this.sewerManholesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.sewerManholesLayers?.length === 0,
          tooltip:
            this.sewerManholesLayers?.length === 0 ? this.$i18n.t('sidebar.upstreamSewage.addManholesToRun') : null,
          ...this.defaultInputProps,
        },
        ...(this.isToolOutpouring
          ? [
              {
                name: 'manhole_elevation_bottom_attribute',
                step: 2,
                connectedLayer: 'manhole_layer',
                translationPath: 'sidebar.upstreamSewage.manholesBottomEndAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['manhole_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'manhole_elevation_top_attribute',
                step: 2,
                connectedLayer: 'manhole_layer',
                translationPath: 'sidebar.upstreamSewage.manholesTerrainAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['manhole_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'manhole_diameter_attribute',
                step: 2,
                connectedLayer: 'manhole_layer',
                translationPath: 'sidebar.upstreamSewage.manholesDiameterAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['manhole_layer'],
                ...this.defaultInputProps,
              },
            ]
          : []),
        {
          name: 'pump_layer',
          step: 3,
          translationPath: 'sidebar.upstreamSewage.pumpsLayer',
          items: this.sewerPumpsLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.sewerPumpsLayers?.length === 0,
          tooltip: this.sewerPumpsLayers?.length === 0 ? this.$i18n.t('sidebar.upstreamSewage.addPumpsToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'partition_layer',
          step: 4,
          isLayer: true,
          translationPath: 'sidebar.upstreamSewage.partitionsLayer',
          items: this.waterPartitionsLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPartitionsLayers?.length === 0,
          tooltip:
            this.waterPartitionsLayers?.length === 0 ? this.$i18n.t('sidebar.upstreamSewage.addPartitionsToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'partition_manholes_attribute',
          step: 4,
          connectedLayer: 'partition_layer',
          translationPath: 'sidebar.upstreamSewage.manholeIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['partition_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_layer',
          step: 5,
          isLayer: true,
          translationPath: 'sidebar.upstreamSewage.watermetersLayer',
          items: this.watermetersLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.watermetersLayers?.length === 0,
          tooltip:
            this.watermetersLayers?.length === 0 ? this.$i18n.t('sidebar.upstreamSewage.addWatermetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_water_usage_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          translationPath: 'sidebar.upstreamSewage.averageConsumptionAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_subwatermeter_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          connectedValue: ['watermeter_subwatermeter_parent_value', 'watermeter_subwatermeter_child_value'],
          translationPath: 'sidebar.upstreamSewage.subcountersAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_subwatermeter_parent_value',
          step: 5,
          connectedAttribute: 'watermeter_subwatermeter_attribute',
          translationPath: 'sidebar.upstreamSewage.mainWatermetersValue',
          disabled: !this.params['watermeter_layer'] || !this.params['watermeter_subwatermeter_attribute'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_subwatermeter_child_value',
          step: 5,
          connectedAttribute: 'watermeter_subwatermeter_attribute',
          translationPath: 'sidebar.upstreamSewage.subcountersValue',
          disabled: !this.params['watermeter_layer'] || !this.params['watermeter_subwatermeter_attribute'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_partition_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          translationPath: 'sidebar.upstreamSewage.partitionPointIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_billing_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          translationPath: 'sidebar.upstreamSewage.watermeterIdBillingSystemAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_parent_billing_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          translationPath: 'sidebar.upstreamSewage.watermeterIdSubwatermeterAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_media_attribute',
          step: 5,
          connectedLayer: 'watermeter_layer',
          connectedValue: [
            'watermeter_media_sewer_values',
            'watermeter_media_water_values',
            'watermeter_media_water_sewer_values',
          ],
          translationPath: 'sidebar.upstreamSewage.mediaAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_media_sewer_values',
          step: 5,
          connectedAttribute: 'watermeter_media_attribute',
          translationPath: 'sidebar.upstreamSewage.sewerMediaValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeter_layer'] || !this.params['watermeter_media_attribute'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_media_water_values',
          step: 5,
          connectedAttribute: 'watermeter_media_attribute',
          translationPath: 'sidebar.upstreamSewage.waterMediaValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeter_layer'] || !this.params['watermeter_media_attribute'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeter_media_water_sewer_values',
          step: 5,
          connectedAttribute: 'watermeter_media_attribute',
          translationPath: 'sidebar.upstreamSewage.sewerWaterMediaValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeter_layer'] || !this.params['watermeter_media_attribute'],
          ...this.defaultInputProps,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep {
  .v-stepper--alt-labels .v-stepper__header .v-stepper__step {
    min-width: 90px !important;
  }
}
</style>
